import {
    Button,
    DialogActions,
    DialogContent,
    Stack,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useFormikContext } from 'formik';
import React, { useMemo } from 'react';

import FormField from 'components/common/formikForm/formField';
import FormGridLayout from 'components/common/formikForm/formGridLayout';
import SubmitButton from 'components/common/formikForm/SubmitButton';

import { useProgramList } from 'hooks/useProgramList';
import useTranslate from 'hooks/useTranslate';
import type { EligibilityFormInterface } from 'types';

interface EligibilityFormFieldsProps {
    countryOptions: string[];
    loading: boolean;
    onClose: () => void;
}

const EligibilityFormFields: React.FC<EligibilityFormFieldsProps> = ({
    countryOptions,
    loading,
    onClose,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const { translate } = useTranslate();
    const { values, setFieldValue } = useFormikContext<EligibilityFormInterface>();

    const countrySelectOptions = useMemo(
        () => countryOptions.map((name) => ({ label: name, value: name })),
        [countryOptions]
    );

    const {
        data: programs = [],
        isLoading: programsLoading,
        isFetching: programsFetching,
    } = useProgramList(values.country);

    const programSelectOptions = useMemo(
        () => programs.map((p) => ({ label: p.label, value: p.value })),
        [programs]
    );

    const programsBusy = programsLoading || programsFetching;
    const serviceDisabled = !values.country || programsBusy || programs.length === 0;

    return (
        <>
            <DialogContent
                dividers
                sx={{
                    pt: 3,
                    pb: 0,
                    flex: '1 1 auto',
                    overflowY: 'auto',
                }}
            >
                <Stack spacing={0.5}>
                    <FormField
                        required
                        type="select"
                        size="small"
                        name="country"
                        label="country"
                        options={countrySelectOptions}
                        disabled={loading}
                        onChange={() => {
                            void setFieldValue('program_name', '');
                        }}
                    />

                    <FormField
                        required
                        type="autosuggest"
                        size="small"
                        name="program_name"
                        label="service"
                        options={programSelectOptions}
                        disabled={serviceDisabled || loading}
                    />
                    {!values.country ? (
                        <Typography variant="caption" color="text.secondary">
                            {translate('selectCountryFirst')}
                        </Typography>
                    ) : programsBusy ? null : programs.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                            {translate('noServicesAvailable')}
                        </Typography>
                    ) : null}

                    <FormGridLayout gap={isMobile ? 0 : 2}>
                        <FormField
                            type="checkbox"
                            size="small"
                            name="enhanced_with_job_offers"
                            label="addJobOffer"
                            disabled={loading}
                        />
                        <FormField
                            type="checkbox"
                            size="small"
                            name="enhanced_language"
                            label="enhancedLanguageSkills"
                            disabled={loading}
                        />
                    </FormGridLayout>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ flexShrink: 0, gap: 1 }}>
                <Button
                    onClick={onClose}
                    color="inherit"
                    disabled={loading}
                    sx={{ textTransform: 'initial' }}
                >
                    {translate('close')}
                </Button>
                <SubmitButton loading={loading} disabled={!values.country || !values.program_name}>
                    {loading ? 'checking' : 'checkEligibility'}
                </SubmitButton>
            </DialogActions>
        </>
    );
};

export default EligibilityFormFields;
